import React, { useState } from 'react';
import { Input, Select, DatePicker, Btn } from '../UI';
import { fmt } from '../../utils';
import {
  ALLOCATION_BASIS_OPTIONS, RECOVERY_METHOD_OPTIONS,
  INPUT_MODE_AMOUNT, INPUT_MODE_PERCENT, MANUAL, SINGLE_LINE,
} from './advanceAllocation';

const NAVY = '#0b318b';
const CTRL = { fontSize: 11, padding: '6px 9px', borderRadius: 7, minHeight: 32, boxSizing: 'border-box' };
const LBL = { fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const CELL = { border: '1px solid #cbd5e1', padding: '5px 8px', fontSize: 11 };
const HEAD = { ...CELL, background: '#243b64', color: '#fff', fontWeight: 700, fontSize: 10, letterSpacing: 0.3, textTransform: 'uppercase' };

const Stat = ({ label, value, tone }) => (
  <div style={{ flex: 1, minWidth: 150, border: '1px solid #cbd5e1', borderRadius: 6, padding: '7px 10px', background: '#fff' }}>
    <div style={LBL}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 800, color: tone || '#0f172a' }}>{fmt(value)}</div>
  </div>
);

/**
 * The Advance Payment panel.
 *
 * An advance pays money ahead of delivery, so it consumes the requisition's
 * approved VALUE without consuming any QUANTITY. That is why the process-qty
 * column goes read-only at zero while this is on, and why the voucher's
 * subtotal comes from the allocations below rather than quantity x rate.
 *
 * Every figure shown here is recomputed and re-checked by the server on save;
 * this panel exists so the user sees the outcome before they commit, not to
 * be the authority on it.
 */
export default function AdvancePaymentPanel({
  advance, setAdvance, selectedLines, allocations, totals, issues, onApply, applied,
}) {
  const [showSupporting, setShowSupporting] = useState(false);
  const set = (k, v) => setAdvance(prev => ({ ...prev, [k]: v }));

  const isManual = advance.allocation_basis === MANUAL;
  const isSingle = advance.allocation_basis === SINGLE_LINE;
  const byAmount = advance.input_mode !== INPUT_MODE_PERCENT;

  // Percent is expressed against the eligible purchase value of the selected
  // lines — the same base the ceiling check uses, so 100% is always exactly
  // the most that could be advanced.
  const onAmount = (v) => {
    const amount = Number(v) || 0;
    const base = totals.eligiblePurchaseValue;
    set('amount', v);
    set('percent', base > 0 ? Math.round((amount / base) * 10000) / 100 : 0);
  };
  const onPercent = (v) => {
    const pct = Number(v) || 0;
    set('percent', v);
    set('amount', Math.round(totals.eligiblePurchaseValue * pct) / 100);
  };

  return (
    <div style={{ border: '1px solid #93c5fd', background: '#f8fbff', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: NAVY, letterSpacing: 0.4 }}>ADVANCE PAYMENT</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          Enter the agreed advance — allocation tracks across selected lines without forcing a process quantity.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <div>
          <label style={LBL}>Advance Amount</label>
          <Input type="number" value={advance.amount} onChange={onAmount} disabled={!byAmount} placeholder="0.00" style={CTRL} />
        </div>
        <div>
          <label style={LBL}>Advance Percentage</label>
          <Input type="number" value={advance.percent} onChange={onPercent} disabled={byAmount} placeholder="0.00" style={CTRL} />
        </div>
        <div>
          <label style={LBL}>Input Mode</label>
          <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 7, overflow: 'hidden', minHeight: 32 }}>
            {[[INPUT_MODE_AMOUNT, 'Enter Amount'], [INPUT_MODE_PERCENT, 'Enter %']].map(([mode, label]) => (
              <button
                key={mode} type="button" onClick={() => set('input_mode', mode)}
                style={{
                  flex: 1, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: advance.input_mode === mode ? NAVY : '#fff',
                  color: advance.input_mode === mode ? '#fff' : '#475569',
                }}
              >{label}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={LBL}>Allocation Basis</label>
          <Select value={advance.allocation_basis} onChange={v => set('allocation_basis', v)} options={ALLOCATION_BASIS_OPTIONS} style={CTRL} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Stat label="Eligible Purchase Value" value={totals.eligiblePurchaseValue} />
        <Stat label="Previous Advance Paid" value={totals.previousAdvancePaid} />
        <Stat label="Processed Value (Selected)" value={totals.processedValue} tone="#2563eb" />
        <Stat label="Remaining Eligible Advance" value={totals.remainingEligible} tone="#c2410c" />
      </div>

      <div>
        <label style={LBL}>Advance Payment Reason <span style={{ color: 'var(--red)' }}>*</span></label>
        <Input
          value={advance.reason}
          onChange={v => set('reason', v)}
          placeholder="Why is this advance being paid?"
          style={{ ...CTRL, ...((advance.reason || '').trim() ? {} : { border: '1px solid var(--red)' }) }}
          title={(advance.reason || '').trim() ? undefined : 'This field is required'}
        />
      </div>

      <div>
        <button
          type="button" onClick={() => setShowSupporting(s => !s)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 800, color: NAVY, letterSpacing: 0.3 }}
        >{showSupporting ? '⌄' : '›'}&nbsp; ADVANCE SUPPORTING INFORMATION</button>

        {showSupporting && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginTop: 10 }}>
            <div><label style={LBL}>Supplier Advance Request Ref</label>
              <Input value={advance.supplier_advance_request_ref} onChange={v => set('supplier_advance_request_ref', v)} style={CTRL} /></div>
            <div><label style={LBL}>Advance Agreement Date</label>
              <DatePicker value={advance.advance_agreement_date} onChange={v => set('advance_agreement_date', v)} style={CTRL} /></div>
            <div><label style={LBL}>Expected Delivery Date</label>
              <DatePicker value={advance.expected_delivery_date} onChange={v => set('expected_delivery_date', v)} style={CTRL} /></div>
            <div><label style={LBL}>Recovery Method</label>
              <Select value={advance.recovery_method} onChange={v => set('recovery_method', v)} options={RECOVERY_METHOD_OPTIONS} style={CTRL} /></div>
            <div><label style={LBL}>Advance Guarantee Reference</label>
              <Input value={advance.advance_guarantee_reference} onChange={v => set('advance_guarantee_reference', v)} style={CTRL} /></div>
            <div><label style={LBL}>Performance Bond Reference</label>
              <Input value={advance.performance_bond_reference} onChange={v => set('performance_bond_reference', v)} style={CTRL} /></div>
            <div style={{ gridColumn: 'span 2' }}><label style={LBL}>Supporting Attachment</label>
              <Input value={advance.advance_supporting_attachment} onChange={v => set('advance_supporting_attachment', v)} placeholder="Document filename / link" style={CTRL} /></div>
          </div>
        )}
      </div>

      {selectedLines.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              {['Line', 'Item', 'Approved Qty', 'Approved Value', 'Previous Payment', 'Remaining Balance', 'Allocation Basis', 'Advance Allocated', 'Advance %', 'Remarks']
                .map(h => <th key={h} style={{ ...HEAD, textAlign: 'center' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {selectedLines.map((l, i) => {
                const allocated = allocations[i] || 0;
                const pct = l.approved_value > 0 ? (allocated / l.approved_value) * 100 : 0;
                const over = allocated > (l.eligible_advance_value || 0) + 0.01;
                return (
                  <tr key={l._key}>
                    <td style={{ ...CELL, textAlign: 'center', fontWeight: 700 }}>L{i + 1}</td>
                    <td style={CELL}>{l.description}</td>
                    <td style={{ ...CELL, textAlign: 'center' }}>{l.approved_quantity ?? '—'}</td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{fmt(l.approved_value)}</td>
                    <td style={{ ...CELL, textAlign: 'right', color: '#2563eb' }}>{fmt(l.processed_value)}</td>
                    <td style={{ ...CELL, textAlign: 'right', color: '#c2410c' }}>{fmt(l.eligible_advance_value)}</td>
                    <td style={{ ...CELL, textAlign: 'center' }}>
                      {isSingle ? (
                        <input
                          type="radio" name="advance-single-line" checked={allocated > 0}
                          onChange={() => set('single_line_index', i)}
                        />
                      ) : (ALLOCATION_BASIS_OPTIONS.find(o => o.value === advance.allocation_basis) || {}).label}
                    </td>
                    <td style={{ ...CELL, textAlign: 'right', padding: isManual ? 2 : CELL.padding }}>
                      {isManual ? (
                        <Input
                          type="number" value={advance.manual_values[i] ?? ''}
                          onChange={v => set('manual_values', Object.assign([...advance.manual_values], { [i]: v }))}
                          style={{ ...CTRL, textAlign: 'right', ...(over ? { border: '1px solid var(--red)' } : {}) }}
                          title={over ? 'Exceeds this line’s remaining eligible advance' : undefined}
                        />
                      ) : (
                        <span style={{ fontWeight: 700, color: over ? 'var(--red)' : '#0f172a' }}>{fmt(allocated)}</span>
                      )}
                    </td>
                    <td style={{ ...CELL, textAlign: 'right' }}>{pct.toFixed(2)}%</td>
                    <td style={{ ...CELL, textAlign: 'center', color: '#94a3b8' }}>{over ? 'Over limit' : '—'}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={7} style={{ ...CELL, textAlign: 'right', fontWeight: 800, background: '#f1f5f9' }}>TOTAL ADVANCE ALLOCATED:</td>
                <td style={{ ...CELL, textAlign: 'right', fontWeight: 800, background: '#f1f5f9', color: NAVY }}>
                  {fmt(allocations.reduce((a, b) => a + (Number(b) || 0), 0))}
                </td>
                <td colSpan={2} style={{ ...CELL, background: '#f1f5f9' }} />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {issues.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px' }}>
          {issues.map((m, i) => (
            <div key={i} style={{ fontSize: 11, color: '#b45309', lineHeight: 1.7 }}>⚠ {m}</div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
          Cumulative advance + processed value cannot exceed the remaining approved purchase value.
        </div>
        <Btn size="sm" variant={applied ? 'secondary' : 'primary'} disabled={issues.length > 0} onClick={onApply}>
          {applied ? '✓ Advance Applied' : '✓ Apply Advance'}
        </Btn>
      </div>
    </div>
  );
}
